import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { assignableRoles, canManageUsers, type Role } from "@/lib/roles";

interface StaffRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  department?: string | null;
  status?: string | null;
  created_at?: string;
}

const roleBadge: Record<string, string> = {
  "SUPER ADMIN": "bg-primary-100 text-primary-700",
  ADMIN: "bg-secondary-100 text-secondary-700",
  EDITOR: "bg-accent-100 text-accent-700",
  AUTHOR: "bg-background-100 text-foreground-600",
  "VIDEO MANAGER": "bg-background-100 text-foreground-600",
  "PROGRAM MANAGER": "bg-background-100 text-foreground-600",
  "TECHNICAL ADMIN": "bg-background-100 text-foreground-600",
  "SOCIAL MEDIA MANAGER": "bg-background-100 text-foreground-600",
  MODERATOR: "bg-background-100 text-foreground-600",
};

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase() || "U"
  );
}

interface ModalState {
  mode: "create" | "edit";
  staff?: StaffRow;
}

export default function UsersAdminPage() {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<ModalState | null>(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase.functions.invoke("manage-users", {
        body: { action: "list" },
      });
      if (err) throw err;
      if (!data?.ok) throw new Error(data?.error ?? "Failed to load users");
      setRows((data.users as StaffRow[]) ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const isSuperAdmin = user?.role === "SUPER ADMIN";

  if (!canManageUsers(user?.role ?? null)) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground-950">Users &amp; Roles</h1>
        <p className="text-sm text-foreground-600">
          You do not have permission to manage users. Only Super Admins and Admins can access this page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Users &amp; Roles</h1>
          <p className="text-sm text-foreground-600 mt-1">
            Create staff accounts and assign each one a role with its own permissions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ mode: "create" })}
          className="flex items-center gap-2 bg-primary-600 text-white font-semibold px-4 py-2.5 rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-user-add-line text-sm" />
          </span>
          Add User
        </button>
      </div>

      {notice && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-secondary-50 border border-secondary-200 rounded-md">
          <i className="ri-information-line text-secondary-600 text-sm mt-0.5" />
          <p className="text-sm text-secondary-800">{notice}</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-50 border border-accent-200 rounded-md">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5" />
          <p className="text-sm text-accent-700">{error}</p>
          <button type="button" onClick={() => void load()} className="ml-auto text-xs underline whitespace-nowrap">
            Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_180px_120px_160px] gap-4 px-5 py-3 bg-background-50 border-b border-background-200 text-xs font-semibold uppercase tracking-wide text-foreground-500">
          <span>Staff member</span>
          <span>Role</span>
          <span>Status</span>
          <span className="text-right">Actions</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-team-line text-xl" />
            </div>
            <p className="mt-4 text-sm font-medium text-foreground-900">No staff accounts yet</p>
            <p className="mt-1 text-xs text-foreground-500">Add your first user to start assigning roles.</p>
          </div>
        ) : (
          <ul className="divide-y divide-background-50">
            {rows.map((s) => (
              <li
                key={s.id}
                className="md:grid md:grid-cols-[1fr_180px_120px_160px] gap-4 items-center px-5 py-4 flex flex-col md:flex-row"
              >
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold shrink-0">
                    {initials(s.name ?? "")}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950 truncate">{s.name}</p>
                    <p className="text-xs text-foreground-500 truncate">{s.email}</p>
                  </div>
                </div>

                <div className="w-full md:w-auto mt-2 md:mt-0">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${
                      roleBadge[s.role] ?? "bg-background-100 text-foreground-600"
                    }`}
                  >
                    {s.role}
                  </span>
                </div>

                <div className="w-full md:w-auto mt-1 md:mt-0">
                  <span
                    className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                      s.status === "locked" ? "bg-accent-500" : "bg-green-500"
                    }`}
                  />
                  <span className="text-xs text-foreground-600 capitalize">{s.status ?? "active"}</span>
                </div>

                <div className="w-full md:w-auto flex items-center gap-2 mt-2 md:mt-0 md:justify-end">
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", staff: s })}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-background-200 text-foreground-700 hover:bg-background-50 whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-edit-line text-sm" />
                    </span>
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void toggleLock(s)}
                    disabled={s.id === user?.id}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border whitespace-nowrap disabled:opacity-40 ${
                      s.status === "locked"
                        ? "border-green-200 text-green-700 hover:bg-green-50"
                        : "border-accent-200 text-accent-700 hover:bg-accent-50"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${s.status === "locked" ? "ri-lock-unlock-line" : "ri-lock-line"} text-sm`} />
                    </span>
                    {s.status === "locked" ? "Unlock" : "Lock"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {modal && (
        <UserModal
          mode={modal.mode}
          staff={modal.staff}
          isSuperAdmin={isSuperAdmin}
          currentUserId={user?.id}
          onClose={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setNotice(msg);
            void load();
          }}
        />
      )}
    </div>
  );

  async function toggleLock(s: StaffRow) {
    const next = s.status === "locked" ? "active" : "locked";
    const { data, error: err } = await supabase.functions.invoke("manage-users", {
      body: { action: "update", payload: { id: s.id, status: next } },
    });
    if (err || !data?.ok) {
      setNotice(err?.message ?? data?.error ?? "Failed to update user");
      return;
    }
    setNotice(`${s.name} ${next === "locked" ? "locked" : "unlocked"}.`);
    void load();
  }
}

function UserModal({
  mode,
  staff,
  isSuperAdmin,
  currentUserId,
  onClose,
  onDone,
}: {
  mode: "create" | "edit";
  staff?: StaffRow;
  isSuperAdmin: boolean;
  currentUserId?: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const { user } = useAdminAuth();
  const [name, setName] = useState(staff?.name ?? "");
  const [email, setEmail] = useState(staff?.email ?? "");
  const [password, setPassword] = useState("");
  const [department, setDepartment] = useState(staff?.department ?? "");
  const [role, setRole] = useState<Role>(staff?.role ?? "EDITOR");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const allowedRoles = useMemo(() => assignableRoles(user?.role ?? null), [user?.role]);

  const editingSelf = mode === "edit" && staff?.id === currentUserId;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (mode === "create") {
      const { data, error: err } = await supabase.functions.invoke("manage-users", {
        body: {
          action: "create",
          payload: { name, email, password, department, role },
        },
      });
      setSubmitting(false);
      if (err || !data?.ok) {
        setError(err?.message ?? data?.error ?? "Failed to create user");
        return;
      }
      onDone(`${name} was created and can now sign in.`);
    } else if (staff) {
      const payload: Record<string, unknown> = { id: staff.id, name, department, role };
      if (password.trim()) payload.password = password;
      const { data, error: err } = await supabase.functions.invoke("manage-users", {
        body: { action: "update", payload },
      });
      setSubmitting(false);
      if (err || !data?.ok) {
        setError(err?.message ?? data?.error ?? "Failed to update user");
        return;
      }
      onDone(`${name} was updated.`);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-900/40" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
          <h2 className="text-base font-semibold text-foreground-950">
            {mode === "create" ? "Add User" : "Edit User"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Full name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Jean-Claude Mbarga"
              className="w-full px-3 py-2.5 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={mode === "edit"}
              placeholder="user@ltv.tv"
              className="w-full px-3 py-2.5 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-background-100 disabled:text-foreground-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">
              {mode === "create" ? "Password" : "New password (optional)"}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={mode === "create"}
              minLength={8}
              placeholder={mode === "create" ? "Minimum 8 characters" : "Leave blank to keep current"}
              className="w-full px-3 py-2.5 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Department</label>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g. News Department"
              className="w-full px-3 py-2.5 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={editingSelf}
              className="w-full px-3 py-2.5 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-background-100"
            >
              {allowedRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {!isSuperAdmin && (
              <p className="mt-1.5 text-[11px] text-foreground-500">
                You can assign all roles except Super Admin. Only a Super Admin can create another Super Admin.
              </p>
            )}
            {editingSelf && (
              <p className="mt-1.5 text-[11px] text-accent-600">
                You cannot change your own role.
              </p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-50 border border-accent-200 rounded-md">
              <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5" />
              <p className="text-sm text-accent-700">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium border border-background-300 rounded-md text-foreground-700 hover:bg-background-50 whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white font-semibold px-4 py-2.5 rounded-md hover:bg-primary-700 disabled:opacity-60 whitespace-nowrap"
            >
              {submitting && (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {mode === "create" ? "Create User" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}